import React, { useState } from 'react';
import { Box, TextField, IconButton, Popover } from '@mui/material';
import { Send, EmojiEmotions } from '@mui/icons-material';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { useSocket } from '../../hooks/useSocket';

interface MessageInputProps {
  onSend: (text: string) => void;
  roomId: string;
  userId: string;
}

export const MessageInput = function MessageInput({
  onSend,
  roomId,
  userId
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLButtonElement | null>(
    null
  );

  const { sendTypingStart, sendTypingStop } = useSocket({
    companyId: '', // Will be provided by context
    enabled: true,
    userId
  });

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage('');

      if (isTyping) {
        sendTypingStop(roomId);
        setIsTyping(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);

    // Typing indicator logic
    if (!isTyping) {
      setIsTyping(true);
      sendTypingStart(roomId, 'User'); // TODO: Get actual user name
    }

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout to stop typing indicator after 2 seconds of inactivity
    const timeout = setTimeout(() => {
      setIsTyping(false);
      sendTypingStop(roomId);
    }, 2000);

    setTypingTimeout(timeout);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessage((prev) => prev + emojiData.emoji);
    setEmojiAnchor(null); // Close picker after selection
  };

  const handleOpenEmojiPicker = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setEmojiAnchor(event.currentTarget);
  };

  const handleCloseEmojiPicker = () => {
    setEmojiAnchor(null);
  };

  const isEmojiPickerOpen = Boolean(emojiAnchor);

  return (
    <Box
      sx={{
        borderColor: 'divider',
        borderTop: '1px solid',
        p: 2
      }}
    >
      <Box sx={{ alignItems: 'flex-end', display: 'flex', gap: 1 }}>
        {/* Emoji Picker Button */}
        <IconButton
          onClick={handleOpenEmojiPicker}
          sx={{ color: 'text.secondary' }}
        >
          <EmojiEmotions />
        </IconButton>

        {/* Emoji Picker Popover */}
        <Popover
          anchorEl={emojiAnchor}
          anchorOrigin={{
            horizontal: 'left',
            vertical: 'top'
          }}
          onClose={handleCloseEmojiPicker}
          open={isEmojiPickerOpen}
          transformOrigin={{
            horizontal: 'left',
            vertical: 'bottom'
          }}
        >
          <EmojiPicker onEmojiClick={handleEmojiClick} />
        </Popover>

        {/* Message Input */}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          size="small"
          value={message}
          variant="outlined"
        />

        {/* Send Button */}
        <IconButton
          color="primary"
          disabled={!message.trim()}
          onClick={handleSend}
        >
          <Send />
        </IconButton>
      </Box>
    </Box>
  );
};

export default MessageInput;
